import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { tasksAPI, usersAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';
import CreateTaskModal from '../../components/tasks/CreateTaskModal';
import TaskKanban from './TaskKanban';

// ── Priority colours ─────────────────────────────────────────────────────────
const PRIORITY_COLORS = {
  Critical: { bg: '#fee2e2', text: '#dc2626' },
  High:     { bg: '#ffedd5', text: '#ea580c' },
  Medium:   { bg: '#dbeafe', text: '#2563eb' },
  Low:      { bg: '#f1f5f9', text: '#64748b' },
};

// ── Status colours ───────────────────────────────────────────────────────────
const STATUS_COLORS = {
  'To Do':       { bg: '#f1f5f9', text: '#475569' },
  'In Progress': { bg: '#fef3c7', text: '#d97706' },
  'Review':      { bg: '#ede9fe', text: '#7c3aed' },
  'Done':        { bg: '#dcfce7', text: '#16a34a' },
  'Cancelled':   { bg: '#f3f4f6', text: '#6b7280' },
};

const STATUSES   = ['To Do', 'In Progress', 'Review', 'Done', 'Cancelled'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const PAGE_SIZE  = 20;

// ── Due date display helper ───────────────────────────────────────────────────
function dueDateStyle(dateStr) {
  if (!dateStr) return { text: '', color: '#94a3b8' };
  const d = new Date(dateStr);
  const now = new Date();
  const isOverdue = d < now && d.toDateString() !== now.toDateString();
  const isToday   = d.toDateString() === now.toDateString();
  const text = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  return {
    text,
    color: isOverdue ? '#dc2626' : isToday ? '#d97706' : '#475569',
    weight: (isOverdue || isToday) ? 600 : 400,
  };
}

function Badge({ bg, text, children, style = {} }) {
  return (
    <span style={{ background: bg, color: text, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', ...style }}>
      {children}
    </span>
  );
}

function Avatar({ name }) {
  return (
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#1B2A6B', color: '#C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10, flexShrink: 0 }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

// ── File type icon ────────────────────────────────────────────────────────────
function fileIcon(ext) {
  if (ext === 'pdf') return '📄';
  if (['png','jpg','jpeg'].includes(ext)) return '🖼️';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  return '📎';
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TaskList() {
  const navigate  = useNavigate();
  const { isSupport, isAdminOrAbove, isManager, isSVP, isRCM, isMD, user } = useRole();
  const canViewReports = isAdminOrAbove || isManager || isSVP || isRCM || isMD;

  // Data state
  const [tasks,      setTasks]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [categories, setCategories] = useState([]);
  const [users,      setUsers]      = useState([]);

  // Filter state
  const [quick,      setQuick]      = useState('all');   // all|my|overdue|today|week
  const [status,     setStatus]     = useState('');
  const [priority,   setPriority]   = useState('');
  const [category,   setCategory]   = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [search,     setSearch]     = useState('');

  // UI state
  const [view,       setView]       = useState('list'); // list|kanban
  const [showModal,  setShowModal]  = useState(false);

  // Load categories & users once
  useEffect(() => {
    tasksAPI.getCategories().then(r => setCategories(r.data?.data?.categories || [])).catch(() => {});
    usersAPI.getAll({ limit: 200 }).then(r => setUsers(r.data?.data?.users || [])).catch(() => {});
  }, []);

  // Build API params from filters
  const buildParams = useCallback((p = 1) => {
    const params = { page: p, limit: PAGE_SIZE };
    if (status)     params.status     = status;
    if (priority)   params.priority   = priority;
    if (category)   params.category   = category;
    if (assignedTo) params.assigned_to = assignedTo;
    if (search)     params.search     = search;

    const now = new Date();
    if (quick === 'my') {
      params.assigned_to = user?._id;
    } else if (quick === 'overdue') {
      params.overdue = true;
    } else if (quick === 'today') {
      params.due_date_from = now.toISOString().slice(0, 10);
      params.due_date_to   = now.toISOString().slice(0, 10);
    } else if (quick === 'week') {
      const end = new Date(now); end.setDate(end.getDate() + 7);
      params.due_date_from = now.toISOString().slice(0, 10);
      params.due_date_to   = end.toISOString().slice(0, 10);
    }
    return params;
  }, [quick, status, priority, category, assignedTo, search, user]);

  const fetchTasks = useCallback(async (p = 1, append = false) => {
    setLoading(true);
    try {
      const params = buildParams(p);
      // For "my" quick filter use the dedicated endpoint
      const res = quick === 'my'
        ? await tasksAPI.getMyTasks(params)
        : await tasksAPI.getTasks(params);
      const incoming = res.data?.data?.tasks || [];
      const t = res.data?.data?.total ?? incoming.length;
      setTasks(prev => append ? [...prev, ...incoming] : incoming);
      setTotal(t);
      setPage(p);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [buildParams, quick]);

  // Re-fetch on filter changes (reset to page 1)
  useEffect(() => { fetchTasks(1); }, [quick, status, priority, category, assignedTo, search]); // eslint-disable-line

  const handleLoadMore = () => fetchTasks(page + 1, true);

  const handleComplete = async (taskId) => {
    try {
      await tasksAPI.completeTask(taskId);
      toast.success('Task marked complete');
      fetchTasks(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to complete task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await tasksAPI.deleteTask(taskId);
      toast.success('Task deleted');
      fetchTasks(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const QUICK_FILTERS = [
    { key: 'all',     label: 'All Tasks' },
    { key: 'my',      label: 'My Tasks' },
    { key: 'overdue', label: '🔴 Overdue' },
    { key: 'today',   label: 'Due Today' },
    { key: 'week',    label: 'Due This Week' },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1B2A6B', margin: 0 }}>Tasks</h1>
          <span style={{ background: '#1B2A6B', color: '#C9A84C', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{total}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* View toggle */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
            {[['list', '≡ List'], ['kanban', '⬛ Kanban']].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer', background: view === v ? '#1B2A6B' : 'transparent', color: view === v ? '#C9A84C' : '#64748b', transition: 'all 0.15s' }}>
                {label}
              </button>
            ))}
          </div>
          {/* Reports link — visible to Admin/MD/SVP/RCM/Manager */}
          {canViewReports && (
            <Link to="/tasks/reports"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#1B2A6B', fontWeight: 600, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              📊 Task Reports
            </Link>
          )}
          {/* Create button — hidden for Support Member */}
          {!isSupport && (
            <button onClick={() => setShowModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#C9A84C', color: '#1B2A6B', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 2px 8px rgba(201,168,76,0.3)' }}>
              + Create Task
            </button>
          )}
        </div>
      </div>

      {/* ── Quick Filter Chips ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {QUICK_FILTERS.map(f => (
          <button key={f.key} onClick={() => setQuick(f.key)}
            style={{ padding: '5px 14px', borderRadius: 20, border: `1.5px solid ${quick === f.key ? '#1B2A6B' : '#e2e8f0'}`, background: quick === f.key ? '#1B2A6B' : '#fff', color: quick === f.key ? '#C9A84C' : '#475569', fontWeight: quick === f.key ? 700 : 500, fontSize: 12, cursor: 'pointer', transition: 'all 0.15s' }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginBottom: 16 }}>
        {/* Search */}
        <div style={{ gridColumn: '1 / -1', position: 'relative' }}>
          <input
            style={{ width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#1e293b', boxSizing: 'border-box', outline: 'none' }}
            placeholder="Search by title or task number…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 15 }}>🔍</span>
        </div>

        {/* Status */}
        <select style={selStyle} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* Priority */}
        <select style={selStyle} value={priority} onChange={e => setPriority(e.target.value)}>
          <option value="">All Priority</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        {/* Category */}
        <select style={selStyle} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
        </select>

        {/* Assigned To (admins/managers see everyone) */}
        <select style={selStyle} value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
          <option value="">All Assignees</option>
          {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
        </select>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      {view === 'kanban' ? (
        <TaskKanban
          filters={{ quick, priority, category, assignedTo, search }}
          onTaskClick={id => navigate('/tasks/' + id)}
        />
      ) : (
        <>
          {loading && tasks.length === 0 ? (
            <LoadingState />
          ) : tasks.length === 0 ? (
            <EmptyState onCreateClick={() => !isSupport && setShowModal(true)} isSupport={isSupport} />
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tasks.map(task => (
                  <TaskRow
                    key={task._id}
                    task={task}
                    isSupport={isSupport}
                    isAdminOrAbove={isAdminOrAbove}
                    onView={() => navigate('/tasks/' + task._id)}
                    onEdit={() => navigate('/tasks/' + task._id)}
                    onComplete={() => handleComplete(task._id)}
                    onDelete={() => handleDelete(task._id)}
                  />
                ))}
              </div>

              {/* Load More */}
              {tasks.length < total && (
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <button onClick={handleLoadMore} disabled={loading}
                    style={{ padding: '9px 28px', borderRadius: 8, border: '1.5px solid #1B2A6B', background: '#fff', color: '#1B2A6B', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    {loading ? 'Loading…' : `Load More (${total - tasks.length} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Create Modal ─────────────────────────────────────────────────────── */}
      {showModal && (
        <CreateTaskModal
          onClose={() => setShowModal(false)}
          onCreated={() => fetchTasks(1)}
        />
      )}
    </div>
  );
}

// ── Shared select style ───────────────────────────────────────────────────────
const selStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0',
  fontSize: 12, background: '#fff', color: '#1e293b', cursor: 'pointer', outline: 'none',
};

// ── TaskRow ───────────────────────────────────────────────────────────────────
function TaskRow({ task, isSupport, isAdminOrAbove, onView, onEdit, onComplete, onDelete }) {
  const pColors  = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.Low;
  const sColors  = STATUS_COLORS[task.status] || STATUS_COLORS['To Do'];
  const due      = dueDateStyle(task.due_date);
  const doneCount = (task.subtasks || []).filter(s => s.status === 'Done').length;
  const totalSub  = (task.subtasks || []).length;
  const pct       = task.completion_percentage || 0;

  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      {/* Left block: number + priority + title */}
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', letterSpacing: '-0.02em' }}>{task.task_number}</span>
          <Badge bg={pColors.bg} text={pColors.text}>{task.priority}</Badge>
          {task.lead_id   && <span style={{ fontSize: 10, background: '#e0f2fe', color: '#0369a1', borderRadius: 4, padding: '1px 6px' }}>📋 Lead</span>}
          {task.student_id && <span style={{ fontSize: 10, background: '#fce7f3', color: '#9d174d', borderRadius: 4, padding: '1px 6px' }}>👤 Student</span>}
        </div>
        <button onClick={onView} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', lineHeight: 1.4 }}>{task.title}</span>
        </button>
        {task.category && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{task.category}</div>}
      </div>

      {/* Status */}
      <Badge bg={sColors.bg} text={sColors.text}>{task.status}</Badge>

      {/* Assignee */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: '0 0 auto' }}>
        <Avatar name={task.assigned_to?.name} />
        <span style={{ fontSize: 12, color: '#475569', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.assigned_to?.name || '—'}</span>
      </div>

      {/* Due date */}
      {due.text && (
        <span style={{ fontSize: 12, color: due.color, fontWeight: due.weight, whiteSpace: 'nowrap' }}>📅 {due.text}</span>
      )}

      {/* Progress */}
      <div style={{ flex: '0 0 100px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>{pct}%</span>
          {totalSub > 0 && <span style={{ fontSize: 10, color: '#94a3b8' }}>{doneCount}/{totalSub} sub</span>}
        </div>
        <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#22c55e' : '#1B2A6B', borderRadius: 3, transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
        <ActionBtn onClick={onView} title="View">👁</ActionBtn>
        {!isSupport && <ActionBtn onClick={onEdit} title="Edit">✏️</ActionBtn>}
        {!isSupport && task.status !== 'Done' && task.status !== 'Cancelled' && (
          <ActionBtn onClick={onComplete} title="Mark Complete" color="#16a34a">✅</ActionBtn>
        )}
        {isAdminOrAbove && <ActionBtn onClick={onDelete} title="Delete" color="#dc2626">🗑</ActionBtn>}
      </div>
    </div>
  );
}

function ActionBtn({ onClick, title, color = '#1B2A6B', children }) {
  return (
    <button onClick={onClick} title={title}
      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 14, color, lineHeight: 1 }}>
      {children}
    </button>
  );
}

function LoadingState() {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #C9A84C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
      <div style={{ fontWeight: 600, fontSize: 14 }}>Loading tasks…</div>
    </div>
  );
}

function EmptyState({ onCreateClick, isSupport }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: 12, border: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
      <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 6 }}>No tasks found</div>
      <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>Create your first task to get started.</div>
      {!isSupport && (
        <button onClick={onCreateClick}
          style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#C9A84C', color: '#1B2A6B', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Create Task
        </button>
      )}
    </div>
  );
}
